import { Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Manager } from "socket.io-client";
import {
  ApiError,
  ApiErrorClass,
  Chat as ChatModel,
  GroupChat,
  Message,
  Profile,
} from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import { NoDataIcon } from "src/grading/assets";
import ChatList from "src/grading/components/ChatList/ChatList";
import ChatTrainingSchedule from "src/grading/components/ChatTrainingSchedule/ChatTrainingSchedule";
import ConfirmationModal from "src/grading/components/ConfirmationModal/ConfirmationModal";
import LoadingOverlay from "src/grading/components/LoadingOverlay/LoadingOverlay";
import MessageInput from "src/grading/components/MessageInput/MessageInput";
import MessageItem from "src/grading/components/MessageItem/MessageItem";
import NewChatModal from "src/grading/components/NewChatModal/NewChatModal";
import NewGroupChatModal from "src/grading/components/NewGroupChatModal/NewGroupChatModal";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  selectCurrentAccessToken,
  selectCurrentUser,
} from "src/store/reducers/auth/authSlice";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import { Member } from "src/store/reducers/organizations/organizationsApiSlice";
import {
  trainingApiSlice,
  useDeleteGroupChatMutation,
  useGetChatsQuery,
  useGetGroupChatsQuery,
  useJoinGroupChatMutation,
  useLazyGetAthleteTrainingPlanQuery,
  useLazyGetChatsWithAthleteQuery,
  useLazyGetGroupChatsMessagesQuery,
  useLeaveGroupChatMutation,
  useMarkChatAsReadMutation,
} from "src/store/reducers/training/trainingApiSlice";
import useStyles from "./Chat.styles";

const TABS = {
  MESSAGES: 0,
  GROUPS: 1,
  SETTINGS: 2,
};

const TABS_LABELS = ["Messages", "Groups", "Settings"];

const Chat = () => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(TABS.MESSAGES);
  const [selectedMessage, setSelectedMessage] = useState<ChatModel | null>(
    null,
  );
  const [selectedGroupMessage, setSelectedGroupMessage] =
    useState<GroupChat | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const {
    data: chatsResponse,
    isLoading: isLoadingChats,
    refetch: refetchChats,
  } = useGetChatsQuery();
  const {
    data: groupChatsResponse,
    // isLoading: isLoadingGroupChats,
    // refetch: refetchGroupChats,
  } = useGetGroupChatsQuery();
  const [
    getAthleteTrainingPlan,
    { isFetching: isLoadingAthleteTrainingPlan, data: athleteTrainingPlanData },
  ] = useLazyGetAthleteTrainingPlanQuery();
  const athleteTrainingPlan = athleteTrainingPlanData?.data?.plan;
  const athleteTrainingPlanMeta = athleteTrainingPlanData?.data?.meta;
  const chats = chatsResponse?.data?.chats;
  const groupChats = groupChatsResponse?.data?.groupChats;
  const [openJoinGroupChatModal, setOpenJoinGroupChatModal] = useState<{
    open: boolean;
    groupChat: GroupChat | null;
  }>({
    open: false,
    groupChat: null,
  });
  const [openDeleteGroupChatModal, setOpenDeleteGroupChatModal] = useState<{
    open: boolean;
    groupChat: GroupChat | null;
  }>({
    open: false,
    groupChat: null,
  });
  const [deleteGroupChat, { isLoading: isLoadingDeleteGroupChat }] =
    useDeleteGroupChatMutation();
  const [leaveGroupChat, { isLoading: isLoadingLeaveGroupChat }] =
    useLeaveGroupChatMutation();
  const [openLeaveGroupChatModal, setOpenLeaveGroupChatModal] = useState<{
    open: boolean;
    groupChat: GroupChat | null;
  }>({
    open: false,
    groupChat: null,
  });

  // Filter chats based on search query
  const filteredChats = chats?.filter((chat) =>
    chat.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const filteredGroupChats = groupChats?.filter((groupChat) =>
    groupChat.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const profile: Profile = useAppSelector(selectCurrentUser);
  const token = useAppSelector(selectCurrentAccessToken);
  const [getChatsWithAthlete, { data: chatsWithAthleteData }] =
    useLazyGetChatsWithAthleteQuery();
  const [getGroupChatsMessages, { data: groupChatsMessagesData }] =
    useLazyGetGroupChatsMessagesQuery();
  const [realtimeMessages, setRealtimeMessages] = useState<Message[]>([]);
  const chatsWithAthlete =
    chatsWithAthleteData?.data?.messages
      ?.slice()
      .reverse()
      .concat(realtimeMessages) || realtimeMessages;

  const groupChatsMessages =
    groupChatsMessagesData?.data?.messages
      ?.slice()
      .reverse()
      .concat(realtimeMessages) || realtimeMessages;
  const dispatch = useAppDispatch();
  const [markChatAsRead] = useMarkChatAsReadMutation();
  const [newGroupChatModal, setNewGroupChatModal] = useState(false);
  const [newChatModal, setNewChatModal] = useState(false);
  const [joinGroupChat, { isLoading: isLoadingJoinGroupChat }] =
    useJoinGroupChatMutation();

  const scrollToBottom = async () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatsWithAthlete]);

  useEffect(() => {
    const manager = new Manager(process.env.REACT_APP_WEB_SOCKET_URL, {
      extraHeaders: {
        authentication: `Bearer ${token}`,
      },
    });

    const socket = manager.socket("/");

    socket.on("chats", (message: Message) => {
      // Check if message is from another user and for the currently selected chat
      if (
        message.senderId !== profile.id &&
        selectedMessage &&
        message.senderId === selectedMessage.userId
      ) {
        setRealtimeMessages((prevMessages) => [...prevMessages, message]);
      }
      trainingApiSlice.util.invalidateTags(["Chats"]);
      refetchChats();
    });

    socket.on("group-chats", (message: Message) => {
      if (message.senderId !== profile?.id) {
        // Only add to realtime messages if it's for the currently selected group chat
        // if (selectedGroupMessage && message.receiverId !== profile.id) {
        setRealtimeMessages((prevMessages) => [...prevMessages, message]);
        // }
        trainingApiSlice.util.invalidateTags(["Chats"]);
        refetchChats();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [profile?.id, selectedMessage, selectedGroupMessage, token, refetchChats]);

  // Reset realtime messages when switching chats or when API data changes
  useEffect(() => {
    setRealtimeMessages([]);
  }, [
    selectedMessage?.userId,
    selectedGroupMessage?.id,
    chatsWithAthleteData,
    groupChatsMessagesData,
  ]);

  const handleSelectMessage = async (chat: ChatModel) => {
    if (
      chat.userId === selectedMessage?.userId &&
      chat.lastMessageDate === selectedMessage?.lastMessageDate
    ) {
      setSelectedMessage(null);
      return;
    }
    try {
      setSelectedGroupMessage(null);
      setSelectedMessage(chat);

      const [, trainingPlanResponse] = await Promise.all([
        getChatsWithAthlete(chat.userId).unwrap(),
        getAthleteTrainingPlan(chat.userId).unwrap(),
        // Mark chat as read when user opens the conversation
        chat.unreadCount > 0
          ? markChatAsRead(chat.userId).unwrap()
          : Promise.resolve(),
      ]);

      if (trainingPlanResponse?.data === null) {
        return;
      }
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    }
  };

  const handleSelectGroupMessage = async (groupChat: GroupChat) => {
    if (!groupChat.member) {
      setOpenJoinGroupChatModal({
        open: true,
        groupChat,
      });
      return;
    }

    if (groupChat.id === selectedGroupMessage?.id) {
      setSelectedGroupMessage(null);
      return;
    }
    try {
      setSelectedMessage(null);
      setSelectedGroupMessage(groupChat);
      await getGroupChatsMessages(groupChat.id).unwrap();
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    }
  };

  const handleOpenDeleteGroupChatModal = (groupChat: GroupChat) => {
    setOpenDeleteGroupChatModal({
      open: true,
      groupChat,
    });
  };

  const handleOpenLeaveGroupChatModal = (groupChat: GroupChat) => {
    setOpenLeaveGroupChatModal({
      open: true,
      groupChat,
    });
  };

  const handleLeaveGroupChat = async () => {
    if (!openLeaveGroupChatModal.groupChat) {
      throw new ApiErrorClass({
        message: "Group chat is required",
      });
    }
    try {
      await leaveGroupChat(
        openLeaveGroupChatModal.groupChat?.id || "",
      ).unwrap();
      setOpenLeaveGroupChatModal({ open: false, groupChat: null });
      setSelectedGroupMessage(null);
      dispatch(
        showSnackbar({
          open: true,
          message: "Successfully left group chat",
          type: "success",
        }),
      );
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    }
  };

  const handleDeleteGroupChat = async () => {
    try {
      await deleteGroupChat(
        openDeleteGroupChatModal.groupChat?.id || "",
      ).unwrap();
      setOpenDeleteGroupChatModal({ open: false, groupChat: null });
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    }
  };

  const handleJoinGroupChat = async () => {
    if (!openJoinGroupChatModal.groupChat) {
      throw new ApiErrorClass({
        message: "Group chat is required",
      });
    }
    try {
      const response = await joinGroupChat(
        openJoinGroupChatModal.groupChat.id,
      ).unwrap();
      if (!response.success) {
        throw new ApiErrorClass({
          message: response.message,
        });
      }
      handleSelectGroupMessage({
        ...openJoinGroupChatModal.groupChat,
        member: true,
      });
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    } finally {
      setOpenJoinGroupChatModal({ open: false, groupChat: null });
    }
  };

  const handleSelectMemberForNewChat = async (member: Member) => {
    try {
      const newChat: ChatModel = {
        userId: member.memberId,
        name: `${member.firstName} ${member.lastName}` || member.email,
        lastMessage: "",
        lastMessageDate: new Date().toISOString(),
        unreadCount: 0,
        avatarUrl: "",
      };
      await handleSelectMessage(newChat);
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    }
  };

  return (
    <Grid container rowGap={6}>
      {/* HEADER */}
      <Grid container columnGap={5} alignItems={"center"}>
        <Typography variant="h1">Chat</Typography>

        {/* TABS */}
        <Tabs
          value={tabValue}
          onChange={(event, newValue) => {
            setSelectedMessage(null);
            setSelectedGroupMessage(null);
            setTabValue(newValue);
          }}
          className={classes.tabs}
          TabIndicatorProps={{
            style: { display: "none" },
          }}
        >
          <Tab className={classes.tab} label={TABS_LABELS[TABS.MESSAGES]} />
          <Tab className={classes.tab} label={TABS_LABELS[TABS.GROUPS]} />
          {/* <Tab className={classes.tab} label={TABS_LABELS[TABS.SETTINGS]} /> */}
        </Tabs>
      </Grid>

      <Grid container spacing={2} sx={{ height: "calc(100vh - 200px)" }}>
        {/* Left Sidebar - Athlete Messages */}
        <Grid item xs={3}>
          <Grid container direction="column" sx={{ height: "100%" }} rowGap={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Athlete messages
            </Typography>

            {/* Search Bar */}
            <Grid item>
              <TextField
                fullWidth
                placeholder="Search by user"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            {tabValue === TABS.MESSAGES && (
              <Grid container justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setNewChatModal(true)}
                >
                  New Chat
                </Button>
              </Grid>
            )}
            {tabValue === TABS.GROUPS && (
              <Grid container justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setNewGroupChatModal(true)}
                >
                  New Group Chat
                </Button>
              </Grid>
            )}

            {/* Athletes List */}
            <ChatList
              chats={
                tabValue === TABS.MESSAGES ? filteredChats : filteredGroupChats
              }
              isLoading={isLoadingChats}
              selectedMessage={selectedMessage}
              selectedGroupMessage={selectedGroupMessage}
              onSelectMessage={
                tabValue === TABS.MESSAGES
                  ? handleSelectMessage
                  : handleSelectGroupMessage
              }
              isGroup={tabValue === TABS.GROUPS}
              handleOpenDeleteGroupChatModal={handleOpenDeleteGroupChatModal}
              handleOpenLeaveGroupChatModal={handleOpenLeaveGroupChatModal}
            />
          </Grid>
        </Grid>

        {/* Center - Chat Conversation */}
        <Grid item xs={6}>
          <Grid
            container
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              rowGap: 2,
            }}
          >
            {selectedMessage || selectedGroupMessage ? (
              <>
                {/* Chat Header */}
                <Typography variant="h3">
                  {selectedMessage?.name || selectedGroupMessage?.name}
                </Typography>

                {/* Messages Area */}
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    overflow: "auto",
                    border: "1px solid #C2C2C2",
                    borderRadius: "8px",
                    maxHeight: "calc(100vh - 350px)",
                    minHeight: "400px",
                    scrollbarWidth: "none",
                  }}
                >
                  {tabValue === TABS.MESSAGES &&
                    selectedMessage &&
                    chatsWithAthlete?.map((message, i) => (
                      <MessageItem message={message} key={i} />
                    ))}
                  {tabValue === TABS.GROUPS &&
                    selectedGroupMessage &&
                    groupChatsMessages?.map((message, i) => (
                      <MessageItem message={message} key={i} />
                    ))}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <MessageInput
                  athleteId={selectedMessage?.userId}
                  isGroupChat={tabValue === TABS.GROUPS}
                  groupChatId={selectedGroupMessage?.id}
                />
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Select an athlete to start chatting
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Right Sidebar - Training Plan */}
        <Grid item xs={3}>
          <Grid container direction="column" sx={{ height: "100%" }}>
            {selectedMessage && (
              <>
                {/* Profile Buttons */}
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.planButton}
                      component={Link}
                      to={`/grading/athletes/${selectedMessage.userId}`}
                    >
                      Plan
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.planButton}
                      component={Link}
                      to={{
                        pathname: `/grading/athletes/${selectedMessage.userId}`,
                        state: { tab: 2 },
                      }}
                    >
                      Profile
                    </Button>
                  </Grid>
                </Grid>

                {athleteTrainingPlan && athleteTrainingPlanMeta ? (
                  <ChatTrainingSchedule
                    trainingPlan={athleteTrainingPlan}
                    meta={athleteTrainingPlanMeta}
                    receiverId={selectedMessage.userId}
                  />
                ) : isLoadingAthleteTrainingPlan ? (
                  <LoadingOverlay />
                ) : (
                  <Grid container>
                    <Grid
                      container
                      flexDirection={"column"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      gap={2}
                    >
                      <NoDataIcon />
                      <Typography variant="subtitle1">
                        There is no data to display yet.
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
      <NewChatModal
        open={newChatModal}
        onClose={() => setNewChatModal(false)}
        onSelectMember={handleSelectMemberForNewChat}
      />
      <NewGroupChatModal
        open={newGroupChatModal}
        onClose={() => setNewGroupChatModal(false)}
      />
      <ConfirmationModal
        open={openDeleteGroupChatModal.open}
        onClose={() =>
          setOpenDeleteGroupChatModal({ open: false, groupChat: null })
        }
        onConfirm={handleDeleteGroupChat}
        title="Delete Group Chat"
        message="Are you sure you want to delete this group chat?"
        confirmationButtonTitle="Delete"
        isLoading={isLoadingDeleteGroupChat}
      />
      <ConfirmationModal
        open={openJoinGroupChatModal.open}
        onClose={() =>
          setOpenJoinGroupChatModal({ open: false, groupChat: null })
        }
        onConfirm={handleJoinGroupChat}
        title="Join Group Chat"
        message="You are not a member of this group. Would you like to join?"
        confirmationButtonTitle="Join"
        type="success"
        isLoading={isLoadingJoinGroupChat}
      />
      <ConfirmationModal
        open={openLeaveGroupChatModal.open}
        onClose={() =>
          setOpenLeaveGroupChatModal({ open: false, groupChat: null })
        }
        onConfirm={handleLeaveGroupChat}
        title="Leave Group Chat"
        message="Are you sure you want to leave this group chat?"
        confirmationButtonTitle="Leave"
        isLoading={isLoadingLeaveGroupChat}
      />
    </Grid>
  );
};

export default Chat;
